import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown, DropdownButton } from "react-bootstrap";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "react-query";
import Swal from "sweetalert2";

import StatusButton from "@modules/Trees/components/Button/StatusButton";
import { Button } from "react-bootstrap";
import TreeImageForm from "@modules/Trees/components/Form/AddForm/TreeImageForm";
import ReactTable from "@components/ReactTable";
import {
  AngleDownIcon,
  EditIcon,
  TrashIcon,
  ImageAddIconSmall,
  FileExportIcon,
  MoreIcon,
} from "@components/icons";

import DownloadExcelButton from "../Button/DownloadExcelButton";
import TreePDFExportPage from "@modules/Trees/components/pdf/PDFExport";
import { statusList } from "../../../../../helper/healthState";
import { treeStatusHeader } from "@modules/Trees/components/TreeList";
import { removeProjectTree } from "@modules/Projects/services/projectCrud";

export default function TreeList({
  trees,
  setAid,
  setFormValue,
  setOpenImageForm,
  openImageForm,
  project,
  setIsUpdate,
  setOpenForm,
  formValue,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const [filterTree, setFilterTree] = useState(trees);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedAreaIds, setSelectedAreaIds] = useState([]);
  const [openCT, setOpenCT] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [ordering, setOrdering] = useState("");
  const tid = query.get("selectedTree");
  const { pid } = useParams();

  // React Query client and mutation for removing trees from project
  const queryClient = useQueryClient();
  const { mutate: projectTreeRemove } = useMutation(removeProjectTree, {
    onSuccess: () => {
      queryClient.invalidateQueries(["project", pid]);
      Swal.fire("Removed!", "Tree has been removed from the project.", "success");
    },
    onError: () => {
      Swal.fire("Error!", "Failed to remove tree from project.", "error");
    },
  });

  // Handle removing tree from project (not deleting the tree completely)
  const handleDelete = (tid) => {
    const data = {
      tree_id: tid,
    };
    Swal.fire({
      html: '<p className="text-center">Are you sure you want to remove this tree from the project?</p>',
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#388090",
      cancelButtonColor: "#EF3600",
      cancelButtonText: "Cancel",
      confirmButtonText: "Remove from Project",
    }).then((result) => {
      if (result.isConfirmed) {
        projectTreeRemove({ data });
      }
    });
  };

  // Rebuild the expanded tree list (child tree expansion), then apply active filters
  const applyFilters = (baseTrees) => {
    let result = [];
    for (const tree of baseTrees) {
      result.push(tree);
      const openPT = localStorage.getItem("openParentTree");
      if (tree.id == openPT && tree.represented_trees) {
        for (const child of tree.represented_trees) {
          result.push(child);
        }
      }
    }
    if (selectedStatus.length > 0) {
      result = result.filter((tree) => selectedStatus.includes(tree.health_state));
    }
    if (selectedAreaIds.length > 0) {
      result = result.filter((tree) => selectedAreaIds.includes(String(tree.areaId || "")));
    }
    return result;
  };

  useEffect(() => {
    setFilterTree(applyFilters(trees));
  }, [openCT]);

  useEffect(() => {
    setFilterTree(applyFilters(trees));
  }, [selectedStatus, selectedAreaIds, trees]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const areaOptions = useMemo(() => {
    const byId = new Map();
    (trees || []).forEach((tree) => {
      const id = String(tree.areaId || "");
      const name = tree.area || "";
      if (!id || !name || byId.has(id)) return;
      byId.set(id, { id, name });
    });
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [trees]);

  const filterTreesByStatus = (status) => {
    setSelectedStatus((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
    setPage(1);
  };

  const filterTreesByArea = (areaId) => {
    setSelectedAreaIds((prev) =>
      prev.includes(areaId) ? prev.filter((id) => id !== areaId) : [...prev, areaId]
    );
    setPage(1);
  };

  const searchedTrees = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return filterTree;

    return filterTree.filter((tree) => {
      const haystack = [tree.name, tree.area, tree.tree_cadastre_no, tree.species_name]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(term);
    });
  }, [filterTree, search]);

  const sortedTrees = useMemo(() => {
    if (!ordering) return searchedTrees;

    const isDesc = ordering.startsWith("-");
    const key = isDesc ? ordering.slice(1) : ordering;
    const direction = isDesc ? -1 : 1;

    return [...searchedTrees].sort((a, b) => {
      const getValue = (tree) => {
        if (key === "represented_trees") return tree?.represented_trees?.length || 0;
        return tree?.[key] ?? "";
      };

      const aValue = getValue(a);
      const bValue = getValue(b);

      if (typeof aValue === "number" && typeof bValue === "number") {
        return (aValue - bValue) * direction;
      }

      return (
        String(aValue).localeCompare(String(bValue), undefined, {
          numeric: true,
          sensitivity: "base",
        }) * direction
      );
    });
  }, [searchedTrees, ordering]);

  const pagedTrees = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedTrees.slice(start, start + pageSize);
  }, [sortedTrees, page, pageSize]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(sortedTrees.length / pageSize));
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [sortedTrees.length, pageSize, page]);

  const columns = useMemo(
    () => [
      {
        Header: `${t("tree")}`,
        accessor: "name",
        Cell: (props) => {
          let name = `${props.row.original.name}`;
          const represented_trees = props.row.original.represented_trees;
          const class_name = "mb-0";
          let html;
          if (represented_trees) {
            html = <h5 className={`${class_name}`}>{name}</h5>;
          } else {
            html = <h8 className={`${class_name}`}>{name}</h8>;
          }
          return (
            <>
              <div className="d-flex align-items-center gap-3 w-100">
                <div>{html}</div>
              </div>
            </>
          );
        },
      },
      {
        Header: `${t("area")}`,
        accessor: "area",
      },
      {
        Header: `${t("child_trees")}`,
        accessor: "represented_trees",

        Cell: (props) => {
          const treeId = props.row.original.id;
          if (props.row.original.represented_trees) {
            const childTrees = props.row.original.represented_trees.length;
            if (childTrees > 0) {
              return (
                <>
                  {`${childTrees}  `}
                  <Button
                    status={props.row.original.health_state}
                    width="78px"
                    padding="1px 10px"
                    onClick={() => {
                      const openPT = localStorage.getItem("openParentTree");
                      const { id, areaId } = props.row.original;
                      const tid = id;
                      localStorage.setItem("areaId", areaId);
                      const params = new URLSearchParams(location.search);
                      params.set("selectedTree", tid);
                      navigate(`${location.pathname}?${params.toString()}`);
                      setAid(areaId);
                      onRowClicked(props.row);
                      if (treeId != openPT) {
                        setOpenCT(treeId);
                        localStorage.setItem("openParentTree", treeId);
                      } else {
                        setOpenCT(-1);
                        localStorage.setItem("openParentTree", -1);
                      }
                    }}
                  >
                    {`${t("open")}`}
                  </Button>
                </>
              );
            }
            return <>{`${childTrees}`}</>;
          }
          return <></>;
        },
      },
      treeStatusHeader(t),
      {
        Header: `${t("actions")}`,
        accessor: "actions",
        style: { width: "56px", textAlign: "center" },
        disableSortBy: true,
        Cell: (props) => {
          return (
            <div className="d-flex justify-content-center">
              <Dropdown align="end" onClick={(e) => e.stopPropagation()}>
                <Dropdown.Toggle
                  as="button"
                  className="project-tree-actions-toggle entity-detail-control border-0"
                  id={`tree-row-actions-${props.row.original.id}`}
                  title={t("more_actions", "Mehr Aktionen")}
                >
                  <MoreIcon size="18px" />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item
                    onClick={() => {
                      setIsUpdate(true);
                      setOpenForm(true);
                      setFormValue(props.row.original);
                    }}
                  >
                    <EditIcon size="16px" /> {t("edit", "Bearbeiten")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => {
                      setOpenImageForm(true);
                      setFormValue(props.row.original);
                    }}
                  >
                    <ImageAddIconSmall size="16px" /> {t("upload", "Hochladen")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    className="text-danger"
                    onClick={() => {
                      let tid = props.row.original.id;
                      handleDelete(tid);
                    }}
                  >
                    <TrashIcon size="16px" /> {t("remove", "Entfernen")}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          );
        },
      },
    ],
    []
  );

  const onRowClicked = (row) => {
    const { id, areaId } = row.original;
    const tid = id;
    localStorage.setItem("areaId", areaId);
    const params = new URLSearchParams(location.search);
    params.set("selectedTree", tid);
    navigate(`${location.pathname}?${params.toString()}`);
    setAid(areaId);
  };

  const d = new Date();
  const isoDate = d.toISOString();
  const date = isoDate.split("T")[0];
  const downloadTitle = `${project.name}_${date}`;
  const buttons = (
    <Dropdown align="end">
      <Dropdown.Toggle variant="outline-secondary">
        <span className="d-inline-flex align-items-center gap-2">
          <FileExportIcon />
          <span>{t("export", "Export")}</span>
          <AngleDownIcon />
        </span>
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <DownloadExcelButton data={filterTree} title={downloadTitle} renderMode="menu" />
        <TreePDFExportPage trees={filterTree} title={downloadTitle} renderMode="menu" />
      </Dropdown.Menu>
    </Dropdown>
  );

  return (
    <>
      <ReactTable
        buttons={buttons}
        columns={columns}
        data={pagedTrees}
        onRowClicked={onRowClicked}
        activeId={tid}
        pagesize={pageSize}
        size={[10, 20, 30, 40, 50]}
        manualPagination
        pageCount={Math.max(1, Math.ceil(sortedTrees.length / pageSize))}
        currentPage={page}
        onPageChange={setPage}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setPage(1);
        }}
        onSortChange={(nextOrdering) => {
          setOrdering(nextOrdering);
          setPage(1);
        }}
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder={t("search_tree")}
        moreFilter={
          <>
            <DropdownButton
              title={
                <div className="d-flex gap-2">
                  <span>{t("status")}</span>
                  <AngleDownIcon />
                </div>
              }
              variant="btn-transparent"
            >
              {statusList.map((status, i) => (
                <li key={i} className="d-flex align-items-center gap-2 px-2 py-1">
                  <input
                    type="checkbox"
                    onChange={() => filterTreesByStatus(status.status)}
                    selected={selectedStatus.includes(status.status)}
                  />
                  <StatusButton status={status.status} width="12px" height="12px"></StatusButton>{" "}
                  {t(status.label)}
                </li>
              ))}
            </DropdownButton>
            {areaOptions.length > 0 && (
              <DropdownButton
                title={
                  <div className="d-flex gap-2">
                    <span>{t("area")}</span>
                    <AngleDownIcon />
                  </div>
                }
                variant="btn-transparent"
              >
                {areaOptions.map((area) => (
                  <li key={area.id} className="d-flex align-items-center gap-2 px-2 py-1">
                    <input
                      type="checkbox"
                      onChange={() => filterTreesByArea(area.id)}
                      checked={selectedAreaIds.includes(area.id)}
                    />
                    <span>{area.name}</span>
                  </li>
                ))}
              </DropdownButton>
            )}
          </>
        }
      />
      {openImageForm && (
        <TreeImageForm show={openImageForm} setOpenForm={setOpenImageForm} formValue={formValue} />
      )}
    </>
  );
}
